import { SampleCode, SampleCodeManager } from './SampleCodeManager'
import { RouteInfo } from '../router'

export class SampleCodePanel {
  private container: HTMLElement
  private isVisible: boolean = false
  private onSelectCallback?: (code: string) => void
  private routeInfo: RouteInfo | null
  
  constructor(routeInfo: RouteInfo | null = null) {
    this.routeInfo = routeInfo
    this.container = this.createContainer()
    this.setupStyles()
    this.render()
  }
  
  /**
   * コンテナを作成
   */
  private createContainer(): HTMLElement {
    const container = document.createElement('div')
    container.className = 'sample-code-panel'
    
    // ヘッダーに追加
    const header = document.querySelector('.header')
    if (header) {
      // ルート情報に基づいてサンプルボタンを表示/非表示
      if (this.shouldShowSampleButton()) {
        // サンプルボタンを作成
        const sampleButton = document.createElement('button')
        sampleButton.className = 'btn btn--secondary sample-button'
        sampleButton.innerHTML = `
          <span class="btn__icon">📚</span>
          サンプル
        `
        sampleButton.addEventListener('click', () => this.toggle())
        
        const controls = header.querySelector('.header__controls')
        if (controls) {
          controls.insertBefore(sampleButton, controls.firstChild)
        }
      }
    }
    
    document.body.appendChild(container)
    
    // クリック外で閉じる
    document.addEventListener('click', (e) => {
      if (this.isVisible && !container.contains(e.target as Node) && 
          !(e.target as Element).closest('.sample-button')) {
        this.hide()
      }
    })
    
    return container
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .sample-button {
        margin-right: 1rem;
      }
      
      .sample-code-panel {
        position: fixed;
        top: 4rem;
        left: 1rem;
        width: 400px;
        max-width: calc(100vw - 2rem);
        max-height: calc(100vh - 5rem);
        background: white;
        border-radius: 0.5rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        border: 1px solid var(--border-color);
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
        transition: all 0.3s ease;
        visibility: hidden;
        z-index: 1000;
        display: flex;
        flex-direction: column;
      }
      
      .sample-code-panel.visible {
        opacity: 1;
        transform: translateY(0) scale(1);
        visibility: visible;
      }
      
      .sample-panel__header {
        padding: 1rem;
        border-bottom: 1px solid var(--border-color);
        background: var(--surface-color);
        border-radius: 0.5rem 0.5rem 0 0;
      }
      
      .sample-panel__title {
        margin: 0 0 0.5rem 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--text-primary);
      }
      
      .sample-panel__tabs {
        display: flex;
        gap: 0.5rem;
      }
      
      .sample-tab {
        padding: 0.375rem 0.75rem;
        font-size: 0.875rem;
        border: 1px solid var(--border-color);
        border-radius: 0.375rem;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
      }
      
      .sample-tab:hover {
        background: var(--surface-color);
      }
      
      .sample-tab.active {
        background: var(--primary-color);
        color: white;
        border-color: var(--primary-color);
      }
      
      .sample-panel__content {
        padding: 1rem;
        overflow-y: auto;
        flex: 1;
      }
      
      .sample-list {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }
      
      .sample-item {
        border: 1px solid var(--border-color);
        border-radius: 0.375rem;
        padding: 0.75rem;
        cursor: pointer;
        transition: all 0.2s ease;
        background: white;
      }
      
      .sample-item:hover {
        border-color: var(--primary-color);
        box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
        transform: translateY(-2px);
      }
      
      .sample-item.selected {
        border-color: var(--primary-color);
        background: var(--surface-color);
        pointer-events: none;
      }
      
      .sample-item__title {
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: 0.25rem;
        font-size: 0.9375rem;
      }
      
      .sample-item__description {
        font-size: 0.8125rem;
        color: var(--text-secondary);
        margin-bottom: 0.5rem;
        line-height: 1.4;
      }
      
      .sample-item__tags {
        display: flex;
        gap: 0.375rem;
        flex-wrap: wrap;
      }
      
      .sample-tag {
        font-size: 0.75rem;
        padding: 0.125rem 0.5rem;
        background: #e5e7eb;
        border-radius: 0.25rem;
        color: #6b7280;
      }
      
      .sample-item__preview {
        margin-top: 0.5rem;
        padding: 0.5rem;
        background: #f9fafb;
        border-radius: 0.25rem;
        font-family: 'Monaco', 'Menlo', monospace;
        font-size: 0.75rem;
        color: #374151;
        max-height: 60px;
        overflow: hidden;
        position: relative;
      }
      
      .sample-item__preview::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 20px;
        background: linear-gradient(to bottom, transparent, #f9fafb);
      }
      
      .difficulty-badge {
        font-size: 0.75rem;
        padding: 0.125rem 0.5rem;
        border-radius: 0.25rem;
        font-weight: 500;
        margin-left: 0.5rem;
      }
      
      .difficulty-badge--beginner {
        background: #10b981;
        color: white;
      }
      
      .difficulty-badge--intermediate {
        background: #f59e0b;
        color: white;
      }
      
      .difficulty-badge--advanced {
        background: #ef4444;
        color: white;
      }
      
      @media (max-width: 768px) {
        .sample-code-panel {
          width: calc(100vw - 2rem);
          max-height: 70vh;
        }
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * レンダリング
   */
  private render(): void {
    // 特定サンプルモードの場合
    if (this.routeInfo?.type === 'sample' && this.routeInfo.sampleId) {
      const sample = SampleCodeManager.getSampleById(this.routeInfo.sampleId)
      if (sample) {
        this.container.innerHTML = `
          <div class="sample-panel__header">
            <h3 class="sample-panel__title">選択中のサンプル</h3>
          </div>
          <div class="sample-panel__content">
            <div class="sample-list">
              <div class="sample-item selected" data-id="${sample.id}">
                <div class="sample-item__title">
                  ${sample.title}
                  <span class="difficulty-badge difficulty-badge--${sample.difficulty}">
                    ${this.getDifficultyLabel(sample.difficulty)}
                  </span>
                </div>
                <div class="sample-item__description">${sample.description}</div>
                <div class="sample-item__tags">
                  ${sample.tags.map(tag => `<span class="sample-tag">${tag}</span>`).join('')}
                </div>
                <div class="sample-item__preview">${this.escapeHtml(sample.code)}</div>
              </div>
            </div>
          </div>
        `
      }
      return
    }

    // 通常モード（/samplesまたはサンプル選択が許可されている場合）
    const difficulties: Array<{key: 'all' | 'beginner' | 'intermediate' | 'advanced', label: string}> = [
      { key: 'all', label: 'すべて' },
      { key: 'beginner', label: '初級' },
      { key: 'intermediate', label: '中級' },
      { key: 'advanced', label: '上級' }
    ]
    
    this.container.innerHTML = `
      <div class="sample-panel__header">
        <h3 class="sample-panel__title">サンプルコード</h3>
        <div class="sample-panel__tabs">
          ${difficulties.map(d => `
            <button class="sample-tab ${d.key === 'all' ? 'active' : ''}" data-difficulty="${d.key}">
              ${d.label}
            </button>
          `).join('')}
        </div>
      </div>
      <div class="sample-panel__content">
        <div class="sample-list"></div>
      </div>
    `
    
    // タブのイベントリスナー
    const tabs = this.container.querySelectorAll('.sample-tab')
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('active'))
        tab.classList.add('active')
        const difficulty = tab.getAttribute('data-difficulty')
        this.renderSampleList(difficulty as any)
      })
    })
    
    // 初期表示
    this.renderSampleList('all')
  }
  
  /**
   * サンプルリストをレンダリング
   */
  private renderSampleList(difficulty: 'all' | 'beginner' | 'intermediate' | 'advanced'): void {
    const listContainer = this.container.querySelector('.sample-list')
    if (!listContainer) return
    
    const samples = difficulty === 'all' 
      ? SampleCodeManager.getAllSamples()
      : SampleCodeManager.getSamplesByDifficulty(difficulty)
    
    listContainer.innerHTML = samples.map(sample => `
      <div class="sample-item" data-id="${sample.id}">
        <div class="sample-item__title">
          ${sample.title}
          <span class="difficulty-badge difficulty-badge--${sample.difficulty}">
            ${this.getDifficultyLabel(sample.difficulty)}
          </span>
        </div>
        <div class="sample-item__description">${sample.description}</div>
        <div class="sample-item__tags">
          ${sample.tags.map(tag => `<span class="sample-tag">${tag}</span>`).join('')}
        </div>
        <div class="sample-item__preview">${this.escapeHtml(sample.code)}</div>
      </div>
    `).join('')
    
    // クリックイベント
    const items = listContainer.querySelectorAll('.sample-item')
    items.forEach(item => {
      item.addEventListener('click', () => {
        const id = item.getAttribute('data-id')
        if (id) {
          const sample = SampleCodeManager.getSampleById(id)
          if (sample && this.onSelectCallback) {
            this.onSelectCallback(sample.code)
            this.hide()
          }
        }
      })
    })
  }
  
  /**
   * 難易度ラベルを取得
   */
  private getDifficultyLabel(difficulty: string): string {
    const labels: Record<string, string> = {
      beginner: '初級',
      intermediate: '中級',
      advanced: '上級'
    }
    return labels[difficulty] || difficulty
  }
  
  /**
   * HTMLをエスケープ
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
  
  /**
   * 表示/非表示を切り替え
   */
  toggle(): void {
    if (this.isVisible) {
      this.hide()
    } else {
      this.show()
    }
  }
  
  /**
   * パネルを表示
   */
  private show(): void {
    this.isVisible = true
    setTimeout(() => {
      this.container.classList.add('visible')
    }, 10)
  }
  
  /**
   * パネルを非表示
   */
  private hide(): void {
    this.isVisible = false
    this.container.classList.remove('visible')
  }
  
  /**
   * 選択時のコールバックを設定
   */
  setOnSelectCallback(callback: (code: string) => void): void {
    this.onSelectCallback = callback
  }

  /**
   * サンプルボタンを表示すべきかどうか
   */
  private shouldShowSampleButton(): boolean {
    // デフォルトルート（/）では表示しない
    if (!this.routeInfo || this.routeInfo.type === 'default') {
      return false
    }
    // /samplesまたは特定サンプルページで表示
    return this.routeInfo.allowSampleSelection || this.routeInfo.type === 'sample'
  }
}